'use client'

import type { ReactNode } from 'react'
import { useTheme } from 'next-themes'
import { useEffect, useMemo, useState } from 'react'
import { usePathname } from 'next/navigation'
import TopNav from './navbar'
import Sidebar from './sidebar'

interface LayoutProps {
	children: ReactNode
}

export default function Layout({ children }: LayoutProps) {
	const { theme } = useTheme()
	const pathname = usePathname()
	const [mounted, setMounted] = useState(false)
	const [menuState, setMenuState] = useState<'full' | 'collapsed' | 'hidden'>('full')
	const [isHovered, setIsHovered] = useState(false)
	const [isMobile, setIsMobile] = useState(false)

	useEffect(() => {
		setMounted(true)
	}, [])

	useEffect(() => {
		const checkMenuState = () => {
			if (typeof window !== 'undefined') {
				if ((window as any).menuState) {
					setMenuState((window as any).menuState)
				}
				if ((window as any).isHovered !== undefined) {
					setIsHovered((window as any).isHovered)
				}
				if ((window as any).isMobile !== undefined) {
					setIsMobile((window as any).isMobile)
				}
			}
		}

		checkMenuState()
		const interval = setInterval(checkMenuState, 50)
		return () => clearInterval(interval)
	}, [])

	// 7. useMemo - 必须在条件返回之前
	const navTitle = useMemo(() => {
		if (!mounted) return '加载中...'

		const storedData = localStorage.getItem('menuData');
		if (!storedData) return '总代';

		try {
			const menuData = JSON.parse(storedData);
			const currentPath = pathname?.replace(/\/+$/, '').toLowerCase() || '/';

			for (const category of menuData) {
				if (category.href?.replace(/\/+$/, '').toLowerCase() === currentPath) {
					return category.label;
				}
				if (category.items) {
					const item = category.items.find(
						(item: any) =>
							item.href?.replace(/\/+$/, '').toLowerCase() === currentPath
					);
					if (item) return item.label;
				}
			}
		} catch {
			return '菜单数据错误';
		}

		return '总代';
	}, [pathname, mounted]);

	if (!mounted) {
		return null
	}

	// Calculate margin based on menu state and hover - only for desktop
	const getMarginLeft = () => {
		if (isMobile) {
			return '0' // No margin on mobile, sidebar is overlay
		}
		if (menuState === 'hidden') {
			return '0'
		}
		if (menuState === 'collapsed' && isHovered) {
			return '16rem' // 256px - full width
		}
		if (menuState === 'collapsed') {
			return '4rem' // 64px - collapsed width
		}
		return '16rem' // 256px - full width
	}

	return (
		<div className={`flex h-screen ${theme === 'dark' ? 'dark' : ''}`}>
			<Sidebar />
			<div
				className="w-full flex flex-1 flex-col transition-all duration-300 ease-in-out min-w-0"
				style={{
					marginLeft: getMarginLeft(),
				}}
			>
				<header className="h-14 border-b border-gray-200 dark:border-[#1F1F23] flex-shrink-0">
					<TopNav title={navTitle} />
				</header>
				<main className="flex-1 overflow-auto p-3 sm:p-6 bg-white dark:bg-[#0F0F12] min-w-0">{children}</main>
			</div>
		</div>
	)
}